const DESKTOP_QUERY = '(min-width: 990px)';
const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';
const DEPTH = {
  slow: 14,
  mid: 24,
  fast: 38,
};
const POINTER_RANGE = { x: 10, y: 8 };
const TILT_RANGE = { x: 3.6, y: 5.4 };
const LERP = {
  scroll: 0.075,
  pointer: 0.1,
  scale: 0.055,
};
const SETTLE = 0.04;

function lerp(current, target, factor) {
  return current + (target - current) * factor;
}

function settled(current, target) {
  return Math.abs(current - target) < SETTLE;
}

function amountFor(item) {
  const named = item.dataset.bwsParallaxDepth;
  if (named && DEPTH[named] != null) return DEPTH[named];

  const amount = parseFloat(item.dataset.bwsParallaxAmount);
  return Number.isFinite(amount) ? amount : DEPTH.mid;
}

function isSectionMode(item) {
  return item.dataset.bwsParallaxMode === 'section';
}

function isPointerMode(item) {
  return item.dataset.bwsParallaxMode === 'pointer';
}

function sectionProgress(item, rootBounds) {
  const host = item.closest('[data-bws-pdp-section]') || item;
  const bounds = host.getBoundingClientRect();
  const travel = Math.max(bounds.height - rootBounds.height, rootBounds.height * 0.35);
  const raw = (rootBounds.top - bounds.top) / travel;
  return Math.max(0, Math.min(1, raw));
}

class BwsPdpMediaMotion {
  #controller;
  #root;
  #desktop;
  #reduced;
  #state = new WeakMap();
  #raf = 0;
  #needsTick = false;
  #didInitialReveal = false;

  constructor(root) {
    this.#root = root;
    this.#controller = new AbortController();
    this.#desktop = window.matchMedia(DESKTOP_QUERY);
    this.#reduced = window.matchMedia(REDUCE_QUERY);

    const { signal } = this.#controller;
    this.#root.addEventListener('scroll', this.#requestUpdate, { capture: true, passive: true, signal });
    this.#root.addEventListener('wheel', this.#requestUpdate, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#requestUpdate, { passive: true, signal });
    this.#desktop.addEventListener('change', this.#onBreakpointChange, { signal });
    this.#reduced.addEventListener('change', this.#onMotionPrefChange, { signal });

    this.#bindPointerItems();
    this.#setupReveal();
    this.#requestUpdate();
  }

  #items() {
    return [...this.#root.querySelectorAll('[data-bws-parallax-item]')];
  }

  #pointerItems() {
    return [...this.#root.querySelectorAll('[data-bws-parallax-mode="pointer"]')];
  }

  #motionEnabled() {
    return this.#desktop.matches && !this.#reduced.matches;
  }

  #stateFor(item) {
    let state = this.#state.get(item);
    if (!state) {
      state = {
        offset: 0,
        targetOffset: 0,
        x: 0,
        targetX: 0,
        y: 0,
        targetY: 0,
        tiltX: 0,
        targetTiltX: 0,
        tiltY: 0,
        targetTiltY: 0,
        scale: 1.03,
        targetScale: 1.03,
      };
      this.#state.set(item, state);
    }
    return state;
  }

  #requestUpdate = () => {
    this.#needsTick = true;
    if (!this.#raf) this.#raf = requestAnimationFrame(this.#tick);
  };

  #tick = () => {
    this.#raf = 0;
    this.#needsTick = false;
    const busy = this.#update();
    if (busy || this.#needsTick) this.#raf = requestAnimationFrame(this.#tick);
  };

  #onBreakpointChange = () => {
    this.#setupReveal();
    this.#requestUpdate();
  };

  #onMotionPrefChange = () => {
    this.#setupReveal();
    if (!this.#motionEnabled()) this.#clearOffsets();
    this.#requestUpdate();
  };

  #clearOffsets() {
    for (const item of this.#items()) {
      item.style.removeProperty('--bws-parallax-offset');
      item.style.removeProperty('--bws-parallax-x');
      item.style.removeProperty('--bws-parallax-y');
      item.style.removeProperty('--bws-tilt-x');
      item.style.removeProperty('--bws-tilt-y');
      item.style.removeProperty('--bws-media-scale');
    }
  }

  #update() {
    if (!this.#motionEnabled()) {
      this.#clearOffsets();
      return false;
    }

    const rootBounds = this.#root.getBoundingClientRect();
    const viewportCenter = rootBounds.top + rootBounds.height / 2;
    let busy = false;

    for (const item of this.#items()) {
      const amount = amountFor(item);
      const state = this.#stateFor(item);

      if (isSectionMode(item)) {
        const progress = sectionProgress(item, rootBounds);
        state.targetOffset = (progress - 0.5) * amount;
      } else {
        const bounds = item.getBoundingClientRect();
        const inView = bounds.bottom >= rootBounds.top - 80 && bounds.top <= rootBounds.bottom + 80;

        if (inView) {
          const itemCenter = bounds.top + bounds.height / 2;
          const travel = Math.max(rootBounds.height / 2 + bounds.height / 2, 1);
          const progress = Math.max(-1, Math.min(1, (itemCenter - viewportCenter) / travel));
          state.targetOffset = progress * -amount;

        }
      }

      state.offset = lerp(state.offset, state.targetOffset, LERP.scroll);
      state.x = lerp(state.x, state.targetX, LERP.pointer);
      state.y = lerp(state.y, state.targetY, LERP.pointer);
      state.tiltX = lerp(state.tiltX, state.targetTiltX, LERP.pointer);
      state.tiltY = lerp(state.tiltY, state.targetTiltY, LERP.pointer);
      state.scale = lerp(state.scale, state.targetScale, LERP.scale);

      if (settled(state.offset, state.targetOffset)) state.offset = state.targetOffset;
      if (settled(state.x, state.targetX)) state.x = state.targetX;
      if (settled(state.y, state.targetY)) state.y = state.targetY;
      if (settled(state.tiltX, state.targetTiltX)) state.tiltX = state.targetTiltX;
      if (settled(state.tiltY, state.targetTiltY)) state.tiltY = state.targetTiltY;
      if (Math.abs(state.scale - state.targetScale) < 0.001) state.scale = state.targetScale;

      if (
        state.offset !== state.targetOffset ||
        state.x !== state.targetX ||
        state.y !== state.targetY ||
        state.tiltX !== state.targetTiltX ||
        state.tiltY !== state.targetTiltY ||
        state.scale !== state.targetScale
      ) {
        busy = true;
      }

      this.#apply(item, state);
    }

    this.#revealVisible(rootBounds);
    return busy;
  }

  #apply(item, state) {
    if (!isPointerMode(item)) {
      item.style.setProperty('--bws-parallax-offset', `${state.offset.toFixed(2)}px`);
    }

    if (isPointerMode(item)) {
      item.style.setProperty('--bws-media-scale', state.scale.toFixed(4));
    }

    if (isPointerMode(item)) {
      item.style.setProperty('--bws-parallax-x', `${state.x.toFixed(2)}px`);
      item.style.setProperty('--bws-parallax-y', `${state.y.toFixed(2)}px`);
      item.style.setProperty('--bws-tilt-x', `${state.tiltX.toFixed(3)}deg`);
      item.style.setProperty('--bws-tilt-y', `${state.tiltY.toFixed(3)}deg`);
    }
  }

  #bindPointerItems() {
    const { signal } = this.#controller;

    for (const item of this.#pointerItems()) {
      item.addEventListener('pointermove', this.#onPointerMove, { passive: true, signal });
      item.addEventListener('pointerleave', this.#onPointerLeave, { passive: true, signal });
    }
  }

  #onPointerMove = (event) => {
    if (!this.#motionEnabled()) return;

    const item = event.currentTarget;
    const bounds = item.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    const nx = (event.clientX - bounds.left) / bounds.width - 0.5;
    const ny = (event.clientY - bounds.top) / bounds.height - 0.5;
    const state = this.#stateFor(item);
    state.targetX = nx * POINTER_RANGE.x * 2;
    state.targetY = ny * POINTER_RANGE.y * 2;
    state.targetTiltY = nx * TILT_RANGE.y * 2;
    state.targetTiltX = -ny * TILT_RANGE.x * 2;
    state.targetScale = 1.03 + Math.min(0.035, Math.hypot(nx, ny) * 0.06);
    this.#requestUpdate();
  };

  #onPointerLeave = (event) => {
    const state = this.#stateFor(event.currentTarget);
    state.targetX = 0;
    state.targetY = 0;
    state.targetTiltX = 0;
    state.targetTiltY = 0;
    state.targetScale = 1.03;
    this.#requestUpdate();
  };

  #setupReveal() {
    const items = [...this.#root.querySelectorAll('[data-bws-media-reveal]')];

    if (!this.#motionEnabled()) {
      for (const item of items) {
        item.classList.remove('bws-will-reveal');
        item.classList.add('is-revealed');
      }
      return;
    }

    const rootBounds = this.#root.getBoundingClientRect();
    const intro = [];

    for (const item of items) {
      if (this.#didInitialReveal && this.#isInView(item, rootBounds, 48)) {
        item.classList.remove('bws-will-reveal');
        item.classList.add('is-revealed');
        continue;
      }

      item.classList.add('bws-will-reveal');
      item.classList.remove('is-revealed');

      if (!this.#didInitialReveal && this.#isInView(item, rootBounds, 48)) intro.push(item);
    }

    if (this.#didInitialReveal) return;

    this.#didInitialReveal = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        for (const item of intro) {
          item.classList.add('is-revealed');
          item.classList.remove('bws-will-reveal');
        }
      });
    });
  }

  #isInView(item, rootBounds, padding = 32) {
    const bounds = item.getBoundingClientRect();
    return bounds.bottom > rootBounds.top + padding && bounds.top < rootBounds.bottom - padding;
  }

  #revealVisible(rootBounds) {
    const pending = this.#root.querySelectorAll('[data-bws-media-reveal].bws-will-reveal');
    for (const item of pending) {
      if (!this.#isInView(item, rootBounds)) continue;
      item.classList.add('is-revealed');
      item.classList.remove('bws-will-reveal');
    }
  }
}

function initBwsPdpMediaMotion() {
  const root = document.querySelector('.content-for-layout:has(.bws-pdp-shell)');
  if (!root || root.dataset.bwsMediaMotionBound === 'true') return;

  root.dataset.bwsMediaMotionBound = 'true';
  new BwsPdpMediaMotion(root);
}

class BwsPdpParallax extends HTMLElement {
  #resizeObserver;

  connectedCallback() {
    initBwsPdpMediaMotion();
    this.#resizeObserver = new ResizeObserver(this.#updateScrollableState);
    this.#resizeObserver.observe(this);
    for (const child of this.children) this.#resizeObserver.observe(child);
    requestAnimationFrame(this.#updateScrollableState);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  #updateScrollableState = () => {
    const hasOverflow = this.scrollHeight > this.clientHeight + 2;
    this.toggleAttribute('data-scrollable', hasOverflow);
    if (!hasOverflow && this.scrollTop !== 0) this.scrollTop = 0;
  };
}

if (!customElements.get('bws-pdp-parallax')) {
  customElements.define('bws-pdp-parallax', BwsPdpParallax);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBwsPdpMediaMotion, { once: true });
} else {
  initBwsPdpMediaMotion();
}
